

export type ElementType = 'text' | 'table' | 'qr' | 'barcode' | 'stamp' | 'serial' | 'shape' | 'image' | 'signature' | 'drawing';

export interface ColumnDefinition {
  id: string;
  header: string;
  width: number;
  align: 'left' | 'center' | 'right';
  key: 'description' | 'qty' | 'rate' | 'tax' | 'total' | string;
  colType?: 'text' | 'serial' | 'currency' | 'number' | 'date';
  serialStart?: number;
  serialPrefix?: string;
  serialSuffix?: string;
}

export interface TableRow {
  id: string;
  values: Record<string, string | number>; // ID-based mapping or column header mapping
}

export interface StampPreset {
  text: string;
  color: string;
  rotation: number;
  borderWidth: number;
  fontSize: number;
}

export interface SerialSettings {
  prefix: string;
  suffix: string;
  digits: number;
  current: number;
  step: number;
  serialType?: string;
  manualMode?: boolean;
  manualValue?: string;
}

export interface CanvasElement {
  id: string;
  type: ElementType;
  x: number; // X position in px
  y: number; // Y position in px
  width: number; // Width in px
  height: number; // Height in px
  opacity: number; // 0 to 100
  zIndex: number; // Ordering depth
  rotation: number; // In degrees, default 0
// Invoice Serial Format System
invoiceFormat?: string;           // e.g. "{PREFIX}-{FY}/{MON}-{####}"
invoiceFormatPreset?: string;     // e.g. "india_gst", "uae_vat", "us_simple"
invoiceCounter?: number;          // global persistent counter
invoicePrefix?: string;           // e.g. "INV", "GST", "VAT"
invoiceSuffix?: string;           // e.g. "/2026", "-PAID"
  // Style attributes
  color?: string; // Hex color
  backgroundColor?: string; // Hex background color
  borderColor?: string; // Hex border
  borderWidth?: number; // Border weight px
  borderRadius?: number; // Border corner radius px
  fontSize?: number; // font weight px
  fontFamily?: string; // Inter, JetBrains Mono, etc.
  fontWeight?: 'normal' | 'semibold' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  direction?: 'ltr' | 'rtl';
  letterSpacing?: number; // tracking px
  lineHeight?: number; // leading multiplier

  // Element specific attributes
  textContent?: string; // text content for 'text' or 'image' URL
  dynamicFieldKey?: string; // explicit batch/mapping key — bypasses label:colon detection; works on text, qr, and barcode elements

  // Table specific fields
  columns?: ColumnDefinition[];
  rows?: TableRow[];
  taxRate?: number; // custom global tax multiplier (calculated dynamically)
  taxLabel?: string; // custom tax name, e.g. "VAT", "GST", etc.
  secondTaxLabel?: string; // secondary custom tax name, e.g., "Service Tax"
  secondTaxRate?: number; // secondary custom tax rate (%)
  discountRate?: number; // custom discount rate (calculated dynamically)
  shippingCost?: number; // custom global shipping fee (calculated dynamically)

  // Barcode / QR specific fields
  barcodeValue?: string; // input data
  barcodeFormat?: 'CODE128' | 'CODE39' | 'EAN13';
  qrValue?: string; // URL, text, UPI, etc.
  showLabel?: boolean;
  labelPosition?: 'top' | 'bottom';
  label?: string;
  totalsLayout?: 'right' | 'full';
  // Stamp attributes
  stampText?: string;
  stampDraft?: boolean; // toggle vintage distress/noise
  stampShape?: 'circle' | 'oval' | 'rectangle' | 'badge' | 'pill';

  // Serial Number attributes
  serialSettings?: SerialSettings;
  serialStyle?: 'classic_lcd' | 'stamped_red' | 'badge_pill' | 'handwritten' | 'simple_ink';

  // Signature attributes
  signatureName?: string;
  signatureStyle?: 'cursive' | 'classical' | 'modern' | 'casual';

  // Shape specific additions (circle, triangle, line, star)
  // Shape specific
shapeType?: 'rectangle' | 'circle' | 'triangle' | 'star' | 'line' | 'arrow' | 'diamond' | 'pentagon' | 'hexagon' | 'cross';
lineStyle?: 'solid' | 'dashed' | 'dotted' | 'double';
dashSize?: number;
gapSize?: number;
strokeAlign?: 'center' | 'inside' | 'outside';
borderColorHex?: string;

// Fill
fillType?: 'solid' | 'linear' | 'radial' | 'none';
gradientFrom?: string;
gradientTo?: string;
gradientAngle?: number;

// Per-corner radius
borderRadiusTL?: number;
borderRadiusTR?: number;
borderRadiusBL?: number;
borderRadiusBR?: number;

// Shadow
shadowEnabled?: boolean;
shadowColor?: string;
shadowOpacity?: number;
shadowX?: number;
shadowY?: number;
shadowBlur?: number;
shadowSpread?: number;

// Blend & flip
blendMode?: string;
flipX?: boolean;
flipY?: boolean;

// Aspect ratio lock
lockAspectRatio?: boolean;

  // Freehand drawing additions
  drawingPoints?: { x: number; y: number }[];
  arabicContent?: string;
  arabicHeader?: string;

  // Explicit QR/stamp/signature → caption linking. Set once when a
  // template is authored (on the QR/stamp/signature element, pointing to
  // its caption text element's id). PDF export uses this as the primary
  // way to group a QR with its caption instead of guessing from position
  // or text content — old templates without this field simply fall back
  // to the previous behavior.
  captionId?: string;
}
export type PageSizePreset = 'A4' | 'LETTER' | 'LABEL_4X6' | 'LABEL_3X2' | 'THERMAL_80MM' | 'THERMAL_58MM';

export interface PageConfig {
  id: PageSizePreset;
  name: string;
  width: number; // standard physical display width in pixels
  height: number; // standard physical display height in pixels
  unitName: string; // e.g. "A4 Document", "Shipping Label"
}

export const PAGE_PRESETS: Record<PageSizePreset, PageConfig> = {
  A4:          { id: 'A4',          name: 'A4 Standard Document',     width: 794, height: 1123, unitName: '210 x 297 mm' },
  LETTER:      { id: 'LETTER',      name: 'US Letter Document',        width: 816, height: 1056, unitName: '8.5 x 11 in'  },
  LABEL_4X6:   { id: 'LABEL_4X6',   name: '4" x 6" Shipping Label',   width: 450, height: 675,  unitName: '4 x 6 in'     },
  LABEL_3X2:   { id: 'LABEL_3X2',   name: '3" x 2" Retail Price Tag', width: 400, height: 266,  unitName: '3 x 2 in'     },
  THERMAL_80MM:{ id: 'THERMAL_80MM',name: '80mm Thermal Receipt',      width: 302, height: 800,  unitName: '80mm Roll'    },
  THERMAL_58MM:{ id: 'THERMAL_58MM',name: '58mm Thermal Receipt',      width: 219, height: 800,  unitName: '58mm Roll'    },
};

export interface ActiveTemplate {
  name: string;
  pageSize: PageSizePreset;
  elements: CanvasElement[];
  category?: string;
}

export interface DiscountRule {
  id: string;
  label?: string;              // e.g. "Loyal Customer 15+"
  metric: 'visits' | 'spend';  // trigger off visit count or total spend
  threshold: number;           // e.g. 15 (visits) or 200 (spend)
  type: 'percent' | 'fixed';
  value: number;
  visibleOnBill: boolean;      // true = shown as a discount line, false = folded into total silently
  createdAt: string;
  outletId?: number | null;    // null/undefined = applies globally across all outlets
}

export interface ClientAccount {
  id: string;
  name: string;
  email: string;
  company: string;
  phone: string;
  address: string;
  createdAt: string;
  visitCount?: number;
  totalSpend?: number;
  membershipLabel?: string;
  exemptFromAutoDiscount?: boolean; // opt this specific account out of all global rules
  marketingOptOut?: boolean; // opt this specific account out of promo campaign sends
  outletId?: number | null;
}

export interface InvoiceRecord {
  id: string;
  invoiceNumber: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  amount: number;
  status: 'pending' | 'paid' | 'Draft' | 'Sent' | 'Paid' | 'Overdue' | 'Cancelled';
  createdAt: string;
  driveFileId?: string | null;
  driveFileUrl?: string | null;
  elements: CanvasElement[];
  pageSize?: PageSizePreset;
  // Payment tracking attributes
  amountPaid?: number;
  balanceDue?: number;
  paymentDate?: string;
  // Dynamic UPI/Bank payments data
  upiId?: string;
  bankAccountHolder?: string;
  bankAccountNumber?: string;
  bankIfsc?: string;
  bankName?: string;
  currency?: string;
  lastEditedAt?: string;
  payment_mode?: string;
  showAmountInWords?: boolean;
editLog?: { action: string; timestamp: string; details: string; }[];
  outlet_id?: number | null;
}

export interface ActivityLog {
  id: string;
  action: string;
  details: string;
  timestamp: string;
  user_name?: string;
  user_role?: string;
  ip_address?: string;
}

export interface BankConnection {
  id: string;
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  balance: number;
  status: 'connected' | 'disconnected' | 'error';
  createdAt: string;
  currency: string;
}

export interface BankTransaction {
  id: string;
  bankConnectionId: string;
  txnSerialNumber: string;
  type: 'Credit' | 'Debit';
  amount: number;
  clientName: string;
  invoiceNumber: string;
  paymentMode: string;
  status: 'Completed' | 'Pending' | 'Failed';
  timestamp: string;
}


// Extended bank connection fields for manual entry
export interface BankConnectionExtended extends BankConnection {
  // Manual entry fields
  iban?: string;
  swiftBic?: string;
  routingNumber?: string;
  upiId?: string;
  ifscCode?: string;
  branchName?: string;
  bankAddress?: string;
  country?: string;
  accountType?: 'checking' | 'savings' | 'business' | 'current';
  // OAuth fields (Phase 2)
  accessToken?: string;
  oauthProvider?: 'plaid' | 'truelayer' | 'razorpay' | 'lean' | 'manual';
  lastSynced?: string;
  verified?: boolean;
}


export interface InvoiceFormatConfig {
  id: string;
  name: string;
  country: string;
  format: string;
  example: string;
  description: string;
  variables: string[];
}